import React, { useState, useContext, useRef, useEffect } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  Box, Container, Typography, Paper, Button, Stack, TextField,
  CircularProgress, Stepper, Step, StepLabel, IconButton, Chip,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "../../components/Header";
import { submitSupportTicket, addTicketFollowUp } from "../../apollo/server";
import UserContext from "../../context/User";
import { SERVER_URL } from "../../utils/global";

const SUBMIT_TICKET = gql`${submitSupportTicket}`;
const ADD_FOLLOWUP = gql`${addTicketFollowUp}`;

const ISSUE_OPTIONS = [
  { type: "MISSING_ITEM",       label: "An item was missing",        icon: "🍔", queue: "RESTAURANT" },
  { type: "INCORRECT_ITEM",     label: "I got the wrong item",       icon: "❌", queue: "RESTAURANT" },
  { type: "WRONG_ORDER",        label: "My entire order was wrong",  icon: "📦", queue: "RESTAURANT" },
  { type: "FOOD_QUALITY",       label: "Food quality issue",         icon: "👎", queue: "RESTAURANT" },
  { type: "NEVER_ARRIVED",      label: "My order never arrived",     icon: "🚫", queue: "ADMIN" },
  { type: "WRONG_ADDRESS_DROP", label: "Delivered to wrong address", icon: "📍", queue: "ADMIN" },
  { type: "DRIVER_ISSUE",       label: "Issue with the driver",      icon: "🛵", queue: "ADMIN" },
  { type: "ORDER_LATE",         label: "Order was very late",        icon: "🕐", queue: "ADMIN" },
  { type: "OTHER",              label: "Something else",             icon: "💬", queue: "ADMIN" },
];

const FOOD_ISSUE_TYPES = new Set(["MISSING_ITEM", "INCORRECT_ITEM", "WRONG_ORDER", "FOOD_QUALITY"]);
const DELIVERY_ONLY_TYPES = new Set(["NEVER_ARRIVED", "WRONG_ADDRESS_DROP", "DRIVER_ISSUE", "ORDER_LATE", "OTHER"]);
const COMPLETED_STATUSES = new Set(["DELIVERED", "CANCELLED"]);

// Shared design tokens — mirrors the hero gradient used on Wallet / Coupons / Membership.
const HERO_GRADIENT = "linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #1d4ed8 130%)";
const RED = "#CC0000";

// Status filter pills for the order picker.
const STATUS_FILTERS = [
  { key: "ALL", label: "All" },
  { key: "ACTIVE", label: "Active" },
  { key: "DELIVERED", label: "Delivered" },
  { key: "CANCELLED", label: "Cancelled" },
];

function FilterPill({ active, label, count, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1.75, py: 0.75, borderRadius: 999,
        display: "inline-flex", alignItems: "center", gap: 0.75,
        cursor: "pointer", userSelect: "none",
        bgcolor: active ? RED : "rgba(15,23,42,0.05)",
        color: active ? "#fff" : "#0f172a",
        border: `1px solid ${active ? RED : "rgba(15,23,42,0.08)"}`,
        fontWeight: 600, fontSize: "0.82rem",
        transition: "all 0.15s",
        "&:hover": { bgcolor: active ? "#b30000" : "rgba(15,23,42,0.08)" },
      }}
    >
      {label}
      {typeof count === "number" && (
        <Box component="span" sx={{
          ml: 0.25, px: 0.85, py: 0.15, borderRadius: 999,
          fontSize: "0.7rem", fontWeight: 700,
          bgcolor: active ? "rgba(255,255,255,0.22)" : "rgba(15,23,42,0.08)",
          color: active ? "#fff" : "#475569",
        }}>{count}</Box>
      )}
    </Box>
  );
}

// Windowed page list with ellipses, e.g. [1, "…", 4, 5, 6, "…", 42].
function getPaginationRange(current, total, siblingCount = 1) {
  const totalNumbersShown = siblingCount * 2 + 5; // first + last + current + 2 siblings + 2 ellipses
  if (totalNumbersShown >= total) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(current - siblingCount, 1);
  const rightSibling = Math.min(current + siblingCount, total);
  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < total - 1;

  if (!showLeftDots && showRightDots) {
    const leftRange = Array.from({ length: 3 + siblingCount * 2 }, (_, i) => i + 1);
    return [...leftRange, "…", total];
  }
  if (showLeftDots && !showRightDots) {
    const rightCount = 3 + siblingCount * 2;
    const rightRange = Array.from({ length: rightCount }, (_, i) => total - rightCount + i + 1);
    return [1, "…", ...rightRange];
  }
  const middleRange = Array.from({ length: rightSibling - leftSibling + 1 }, (_, i) => leftSibling + i);
  return [1, "…", ...middleRange, "…", total];
}

function statusInfoForOrder(o) {
  if (o.orderStatus === "DELIVERED") return { label: "Delivered", color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" };
  if (o.orderStatus === "CANCELLED") return { label: "Cancelled", color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" };
  return { label: "In progress", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" };
}

export default function GetHelp() {
  const { orderId: urlOrderId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, orders, loadingOrders, fetchOrders } = useContext(UserContext);

  // Orders are no longer fetched at sign-in (see context/User.js), so this
  // screen asks for them itself — it is built entirely around picking one.
  useEffect(() => {
    if (!isLoggedIn || loadingOrders) return;
    if (!orders || orders.length === 0) fetchOrders();
    // Mount-time only: refiring on `orders` would loop for a customer who
    // genuinely has none.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // If no orderId in URL, show order picker first
  const [pickedOrderId, setPickedOrderId] = useState(urlOrderId || null);
  const orderId = pickedOrderId;

  const [step, setStep] = useState(urlOrderId ? 0 : -1); // -1=pick order, -0.5=prior ticket warning, 0=choose issue, 1=details, 2=review, 3=done
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [description, setDescription] = useState("");
  const [affectedItems, setAffectedItems] = useState({}); // itemId -> refundQty
  const [createdTicket, setCreatedTicket] = useState(null);
  const [images, setImages] = useState([]); // [{ preview, url, uploading }]
  const [warningOrder, setWarningOrder] = useState(null); // order that triggered the warning
  const [isFollowUp, setIsFollowUp] = useState(false); // true = adding to existing ticket
  const [statusFilter, setStatusFilter] = useState("ALL");
  const fileInputRef = useRef(null);

  const ticketStatusInfo = (status) => {
    if (status === "RESOLVED") return { label: "Resolved", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" };
    if (status === "CLOSED_NO_ACTION") return { label: "Closed", color: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb" };
    if (status === "OPEN" || status === "AWAITING_RESTAURANT" || status === "AWAITING_ADMIN" ||
        status === "PENDING_RESTAURANT" || status === "PENDING_ADMIN")
      return { label: "In review", color: "#d97706", bg: "#fffbeb", border: "#fde68a" };
    return null;
  };

  const handleOrderPick = (o) => {
    if (o.supportTicketStatus) {
      setWarningOrder(o);
      setPickedOrderId(o._id);
      setStep(-0.5);
    } else {
      setPickedOrderId(o._id);
      setStep(0);
    }
  };

  const BACKEND_URL = SERVER_URL.replace(/\/$/, ""); // strip trailing slash

  const handleImagePick = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (images.length >= 3) break; // max 3 images
      const preview = URL.createObjectURL(file);
      setImages(prev => [...prev, { preview, url: null, uploading: true }]);
      try {
        const formData = new FormData();
        formData.append("image", file);
        const res = await fetch(`${BACKEND_URL}/upload/image`, { method: "POST", body: formData });
        const data = await res.json();
        setImages(prev => prev.map((img, i) =>
          img.preview === preview ? { ...img, url: data.url || data.imageUrl || data.secure_url, uploading: false } : img
        ));
      } catch {
        setImages(prev => prev.filter(img => img.preview !== preview));
      }
    }
    e.target.value = "";
  };

  const order = orders?.find(o => o._id === orderId);
  const items = order?.items || [];

  const PAGE_SIZE = 10;
  const [orderPage, setOrderPage] = useState(1);

  const sortedOrders = (orders || [])
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Counts per filter for the pills.
  const counts = {
    ALL: sortedOrders.length,
    ACTIVE: sortedOrders.filter(o => !COMPLETED_STATUSES.has(o.orderStatus)).length,
    DELIVERED: sortedOrders.filter(o => o.orderStatus === "DELIVERED").length,
    CANCELLED: sortedOrders.filter(o => o.orderStatus === "CANCELLED").length,
  };

  const filteredOrders = sortedOrders.filter(o => {
    if (statusFilter === "ALL") return true;
    if (statusFilter === "ACTIVE") return !COMPLETED_STATUSES.has(o.orderStatus);
    return o.orderStatus === statusFilter;
  });

  const totalOrderPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const recentOrders = filteredOrders.slice((orderPage - 1) * PAGE_SIZE, orderPage * PAGE_SIZE);

  const [submitTicket, { loading: submittingNew }] = useMutation(SUBMIT_TICKET, {
    onCompleted: (data) => {
      setCreatedTicket(data.submitSupportTicket);
      setStep(3);
    },
  });

  const [addFollowUp, { loading: submittingFollowUp }] = useMutation(ADD_FOLLOWUP, {
    onCompleted: () => {
      // Navigate to the existing ticket
      navigate(`/ticket-detail/${warningOrder.supportTicketId}`);
    },
  });

  const submitting = submittingNew || submittingFollowUp;

  const isCompletedOrder = order ? COMPLETED_STATUSES.has(order.orderStatus) : true;
  const visibleIssueOptions = isCompletedOrder
    ? ISSUE_OPTIONS
    : ISSUE_OPTIONS.filter(o => DELIVERY_ONLY_TYPES.has(o.type));

  const isFoodIssue = selectedIssue && FOOD_ISSUE_TYPES.has(selectedIssue.type);

  // Calculate estimated credit from affected items
  const estimatedCredit = isFoodIssue
    ? items.reduce((sum, item) => {
        const qty = affectedItems[item._id] || 0;
        const price = parseFloat(item.variation?.price || 0);
        return sum + qty * price;
      }, 0)
    : parseFloat(order?.orderAmount || 0);

  const buildAffectedItems = () =>
    items
      .filter(i => (affectedItems[i._id] || 0) > 0)
      .map(i => ({
        orderItemId: parseInt(i._id),
        name: i.title,
        qty: i.quantity,
        unitPrice: parseFloat(i.variation?.price || 0),
        refundQty: affectedItems[i._id],
        refundAmount: affectedItems[i._id] * parseFloat(i.variation?.price || 0),
      }));

  const handleSubmit = () => {
    const affected = isFoodIssue ? buildAffectedItems() : [];
    const uploadedUrls = images.filter(i => i.url).map(i => i.url);

    if (isFollowUp && warningOrder?.supportTicketId) {
      addFollowUp({
        variables: {
          ticketId: warningOrder.supportTicketId,
          issueType: selectedIssue.type,
          description: description || null,
          affectedItems: affected.length > 0 ? affected : null,
          evidenceUrls: uploadedUrls.length > 0 ? uploadedUrls : null,
        },
      });
    } else {
      submitTicket({
        variables: {
          orderId: parseInt(orderId),
          issueType: selectedIssue.type,
          description: description || null,
          affectedItems: affected.length > 0 ? affected : null,
          requestedAmount: estimatedCredit > 0 ? estimatedCredit : null,
          evidenceUrls: uploadedUrls.length > 0 ? uploadedUrls : null,
        },
      });
    }
  };

  if (loadingOrders) return (
    <><Header /><Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}><CircularProgress /></Box></>
  );

  // ── Hero banner shared across all steps ─────────────────────────────────
  const Hero = ({ title, subtitle }) => (
    <Box sx={{
      position: "relative",
      background: HERO_GRADIENT,
      color: "#fff",
      overflow: "hidden",
      pt: { xs: 12, md: 14 }, pb: { xs: 9, md: 11 },
    }}>
      {/* Decorative blurred orbs */}
      <Box sx={{
        position: "absolute", top: -80, right: -60, width: 320, height: 320,
        borderRadius: "50%", bgcolor: "rgba(59,130,246,0.28)", filter: "blur(80px)",
      }} />
      <Box sx={{
        position: "absolute", bottom: -120, left: -80, width: 360, height: 360,
        borderRadius: "50%", bgcolor: "rgba(204,0,0,0.18)", filter: "blur(90px)",
      }} />
      <Container maxWidth="md" sx={{ position: "relative" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{
            width: 56, height: 56, borderRadius: "16px",
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.12)", backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}>
            <SupportAgentIcon sx={{ fontSize: 30, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: 1.4, color: "#93c5fd", textTransform: "uppercase" }}>
              RUSHMYFOOD SUPPORT
            </Typography>
            <Typography sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, fontWeight: 800, lineHeight: 1.15, mt: 0.25 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.78)", mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </Container>
    </Box>
  );

  const todayLabel = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <>
      <Header />
      <Box sx={{ bgcolor: "#f8fafc", minHeight: "80vh" }}>
        <Hero
          title={
            step === 3 ? "Report submitted" :
            step === -0.5 ? "Existing ticket found" :
            step === -1 ? "Get help" :
            order ? `Order #${order.orderId}` : "Get help"
          }
          subtitle={
            step === 3 ? "We've got it from here" :
            step === -1 ? `${todayLabel} · Pick the order you need help with` :
            step === -0.5 ? "Choose how you'd like to proceed" :
            selectedIssue ? selectedIssue.label : "Tell us what went wrong"
          }
        />

        {/* Pulled-up content area */}
        <Container maxWidth="md" sx={{ mt: { xs: -6, md: -7 }, position: "relative", pb: 6 }}>

          {/* Step indicator inside a floating card — only on flow steps */}
          {step >= 0 && step < 3 && (
            <Paper sx={{
              p: { xs: 2, md: 2.5 }, borderRadius: "20px", mb: 2.5,
              boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              border: "1px solid rgba(15,23,42,0.05)",
            }}>
              <Stepper activeStep={Math.floor(step)} alternativeLabel sx={{
                "& .MuiStepIcon-root.Mui-active": { color: RED },
                "& .MuiStepIcon-root.Mui-completed": { color: "#16a34a" },
              }}>
                {["Choose issue", "Tell us more", "Review"].map(label => (
                  <Step key={label}>
                    <StepLabel><Typography fontSize="0.78rem" fontWeight={600}>{label}</Typography></StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          )}

          {/* ─── STEP -1: Pick an order ──────────────────────────────────── */}
          {step === -1 && (
            <Paper sx={{
              p: { xs: 2, md: 3 }, borderRadius: "20px",
              boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              border: "1px solid rgba(15,23,42,0.05)",
            }}>
              <Typography fontWeight={800} fontSize="1.05rem" mb={0.5}>
                Which order do you need help with?
              </Typography>
              <Typography fontSize="0.85rem" color="text.secondary" mb={2}>
                You can report issues on any order — active, delivered, or cancelled.
              </Typography>

              {/* Filter pills */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2.5}>
                {STATUS_FILTERS.map(f => (
                  <FilterPill
                    key={f.key}
                    label={f.label}
                    count={counts[f.key]}
                    active={statusFilter === f.key}
                    onClick={() => { setStatusFilter(f.key); setOrderPage(1); }}
                  />
                ))}
              </Stack>

              {recentOrders.length === 0 && (
                <Box sx={{
                  p: 4, textAlign: "center", borderRadius: "14px",
                  border: "1px dashed #cbd5e1", bgcolor: "#f8fafc",
                }}>
                  <ReceiptLongOutlinedIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
                  <Typography color="text.secondary" fontSize="0.92rem" fontWeight={600}>
                    No orders in this filter.
                  </Typography>
                  <Button variant="outlined" onClick={() => navigate("/orders")}
                    sx={{ mt: 2, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
                    View My Orders
                  </Button>
                </Box>
              )}

              {recentOrders.length > 0 && (
                <Typography fontSize="0.78rem" color="text.secondary" mb={1.5}>
                  Showing {(orderPage - 1) * PAGE_SIZE + 1}–{Math.min(orderPage * PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length} orders
                </Typography>
              )}

              <Stack spacing={1.25}>
                {recentOrders.map(o => {
                  const tInfo = o.supportTicketStatus ? ticketStatusInfo(o.supportTicketStatus) : null;
                  const sInfo = statusInfoForOrder(o);
                  const itemCount = o.items?.length || 0;
                  return (
                    <Box key={o._id}
                      onClick={() => handleOrderPick(o)}
                      sx={{
                        position: "relative",
                        p: 2, borderRadius: "14px", cursor: "pointer",
                        bgcolor: "#fff",
                        border: "1px solid #e5e7eb",
                        display: "flex", alignItems: "center", gap: 1.5,
                        transition: "all 0.18s ease",
                        "&:hover": {
                          borderColor: RED,
                          transform: "translateY(-1px)",
                          boxShadow: "0 8px 22px rgba(204,0,0,0.10)",
                        },
                      }}
                    >
                      {/* Left accent stripe */}
                      <Box sx={{
                        position: "absolute", left: 0, top: 8, bottom: 8, width: 4, borderRadius: "0 4px 4px 0",
                        bgcolor: tInfo ? tInfo.color : sInfo.color,
                      }} />
                      <Box sx={{
                        ml: 0.5,
                        width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                        bgcolor: "#eff6ff", color: "#1d4ed8",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <ReceiptLongOutlinedIcon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                          <Typography fontWeight={700} fontSize="0.92rem">Order #{o.orderId}</Typography>
                          <Chip label={sInfo.label} size="small"
                            sx={{ fontSize: "0.65rem", fontWeight: 700, height: 18,
                                  bgcolor: sInfo.bg, color: sInfo.color, border: `1px solid ${sInfo.border}` }} />
                          {tInfo && (
                            <Chip label={tInfo.label} size="small"
                              sx={{ fontSize: "0.65rem", fontWeight: 700, height: 18,
                                    bgcolor: tInfo.bg, color: tInfo.color, border: `1px solid ${tInfo.border}` }} />
                          )}
                        </Stack>
                        <Typography fontSize="0.8rem" color="text.secondary" mt={0.25}>
                          {itemCount} item{itemCount !== 1 ? "s" : ""} · ${parseFloat(o.orderAmount || 0).toFixed(2)}
                          {o.createdAt ? ` · ${new Date(o.createdAt).toLocaleDateString()}` : ""}
                        </Typography>
                      </Box>
                      <ArrowForwardRoundedIcon sx={{ color: "#94a3b8", fontSize: 20, flexShrink: 0 }} />
                    </Box>
                  );
                })}
              </Stack>

              {totalOrderPages > 1 && (
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} pt={2.5} flexWrap="wrap" useFlexGap>
                  <IconButton size="small" disabled={orderPage === 1}
                    onClick={() => setOrderPage(p => p - 1)}
                    sx={{ border: "1px solid #e5e7eb", borderRadius: "10px" }}>
                    <ChevronLeftRoundedIcon fontSize="small" />
                  </IconButton>
                  {getPaginationRange(orderPage, totalOrderPages).map((p, i) =>
                    p === "…" ? (
                      <Typography key={`dots-${i}`} sx={{ px: 0.5, color: "#94a3b8", fontSize: "0.85rem" }}>
                        …
                      </Typography>
                    ) : (
                      <Button key={p} size="small"
                        variant={p === orderPage ? "contained" : "outlined"}
                        onClick={() => setOrderPage(p)}
                        sx={{
                          minWidth: 36, px: 1, borderRadius: "10px", fontSize: "0.78rem",
                          fontWeight: p === orderPage ? 700 : 500,
                          ...(p === orderPage
                            ? { bgcolor: RED, "&:hover": { bgcolor: "#b30000" }, borderColor: "transparent" }
                            : { borderColor: "#e5e7eb", color: "#374151" }),
                        }}>
                        {p}
                      </Button>
                    )
                  )}
                  <IconButton size="small" disabled={orderPage === totalOrderPages}
                    onClick={() => setOrderPage(p => p + 1)}
                    sx={{ border: "1px solid #e5e7eb", borderRadius: "10px" }}>
                    <ChevronRightRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Paper>
          )}

          {/* ─── STEP -0.5: Prior ticket warning ─────────────────────────── */}
          {step === -0.5 && warningOrder && (() => {
            const tInfo = ticketStatusInfo(warningOrder.supportTicketStatus);
            return (
              <Paper sx={{
                p: { xs: 2.5, md: 3 }, borderRadius: "20px",
                boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
                border: "1px solid rgba(15,23,42,0.05)",
              }}>
                <Box sx={{
                  p: 2.5, borderRadius: "14px",
                  border: "1px solid #fde68a", bgcolor: "#fffbeb", mb: 2.5,
                }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <WarningAmberRoundedIcon sx={{ color: "#d97706", mt: 0.2, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700} fontSize="0.95rem" color="#92400e" mb={0.5}>
                        You already filed a report for this order
                      </Typography>
                      <Typography fontSize="0.85rem" color="#78350f" mb={1.5}>
                        Order #{warningOrder.orderId} already has a support ticket
                        {tInfo ? ` — ${tInfo.label}` : ""}.
                        Filing a new report for the same order will create a separate ticket.
                      </Typography>
                      {tInfo && (
                        <Chip label={tInfo.label} size="small"
                          sx={{ fontSize: "0.7rem", fontWeight: 700,
                                bgcolor: tInfo.bg, color: tInfo.color, border: `1px solid ${tInfo.border}` }} />
                      )}
                    </Box>
                  </Stack>
                </Box>

                <Stack spacing={1.25}>
                  {warningOrder.supportTicketId && (
                    <Button variant="outlined" fullWidth
                      onClick={() => navigate(`/ticket-detail/${warningOrder.supportTicketId}`)}
                      sx={{ borderRadius: "12px", fontWeight: 600, textTransform: "none", py: 1.1 }}>
                      View existing ticket
                    </Button>
                  )}
                  <Button variant="contained" fullWidth
                    onClick={() => { setIsFollowUp(true); setStep(0); }}
                    sx={{ borderRadius: "12px", fontWeight: 700, textTransform: "none",
                          bgcolor: RED, py: 1.1, "&:hover": { bgcolor: "#b30000" } }}>
                    Add follow-up to this ticket
                  </Button>
                  <Button variant="text" onClick={() => setStep(-1)}
                    sx={{ borderRadius: "12px", color: "#6b7280", textTransform: "none" }}>
                    ← Choose a different order
                  </Button>
                </Stack>
              </Paper>
            );
          })()}

          {/* ─── STEP 0: Choose issue ────────────────────────────────────── */}
          {step === 0 && (
            <Paper sx={{
              p: { xs: 2, md: 3 }, borderRadius: "20px",
              boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              border: "1px solid rgba(15,23,42,0.05)",
            }}>
              <Typography fontWeight={800} fontSize="1.05rem" mb={2}>What went wrong?</Typography>
              <Stack spacing={1.25}>
                {visibleIssueOptions.map(opt => (
                  <Box key={opt.type}
                    onClick={() => { setSelectedIssue(opt); setStep(1); }}
                    sx={{
                      p: 1.75, borderRadius: "14px", cursor: "pointer",
                      bgcolor: "#fff",
                      border: "1px solid #e5e7eb",
                      display: "flex", alignItems: "center", gap: 1.5,
                      transition: "all 0.18s ease",
                      "&:hover": {
                        borderColor: RED,
                        transform: "translateY(-1px)",
                        boxShadow: "0 8px 22px rgba(204,0,0,0.10)",
                      },
                    }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                      bgcolor: "#f1f5f9",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "1.4rem",
                    }}>{opt.icon}</Box>
                    <Typography fontSize="0.92rem" fontWeight={600} sx={{ flex: 1 }}>{opt.label}</Typography>
                    <ArrowForwardRoundedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                  </Box>
                ))}
              </Stack>
              <Button variant="text" onClick={() => setStep(urlOrderId ? -1 : -1)}
                sx={{ mt: 2, borderRadius: "10px", color: "#6b7280", textTransform: "none" }}>
                ← Choose a different order
              </Button>
            </Paper>
          )}

          {/* ─── STEP 1: Details ─────────────────────────────────────────── */}
          {step === 1 && selectedIssue && (
            <Paper sx={{
              p: { xs: 2, md: 3 }, borderRadius: "20px",
              boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              border: "1px solid rgba(15,23,42,0.05)",
            }}>
              <Stack direction="row" alignItems="center" spacing={1.25} mb={2}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: "10px", bgcolor: "#f1f5f9",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem",
                }}>{selectedIssue.icon}</Box>
                <Typography fontWeight={800} fontSize="1.02rem">{selectedIssue.label}</Typography>
              </Stack>

              {/* Item selector for food issues */}
              {isFoodIssue && items.length > 0 && (
                <Box sx={{
                  p: 2, borderRadius: "14px", mb: 2,
                  bgcolor: "#f8fafc", border: "1px solid #e5e7eb",
                }}>
                  <Typography fontWeight={700} fontSize="0.88rem" mb={1.5}>
                    Which items were affected?
                  </Typography>
                  <Stack spacing={1.2}>
                    {items.map(item => {
                      const qty = affectedItems[item._id] || 0;
                      return (
                        <Stack key={item._id} direction="row" alignItems="center" justifyContent="space-between">
                          <Typography fontSize="0.88rem">{item.title} × {item.quantity}</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton size="small"
                              onClick={() => setAffectedItems(p => ({ ...p, [item._id]: Math.max(0, (p[item._id] || 0) - 1) }))}
                              sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", width: 30, height: 30 }}>−</IconButton>
                            <Typography fontSize="0.9rem" fontWeight={600} width={22} textAlign="center">{qty}</Typography>
                            <IconButton size="small"
                              onClick={() => setAffectedItems(p => ({ ...p, [item._id]: Math.min(item.quantity, (p[item._id] || 0) + 1) }))}
                              sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", width: 30, height: 30 }}>+</IconButton>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              <TextField
                multiline rows={3} fullWidth
                label="Describe the issue (optional)"
                value={description}
                onChange={e => setDescription(e.target.value)}
                sx={{
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "&.Mui-focused fieldset": { borderColor: RED },
                  },
                  "& label.Mui-focused": { color: RED },
                  mb: 2,
                }}
              />

              {/* Image upload */}
              <Box mb={2}>
                <Typography fontWeight={700} fontSize="0.88rem" mb={1}>
                  Add photos <Typography component="span" fontSize="0.78rem" color="text.secondary" fontWeight={400}>(optional, up to 3)</Typography>
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {images.map((img, i) => (
                    <Box key={i} sx={{ position: "relative", width: 80, height: 80 }}>
                      <Box
                        component="img"
                        src={img.preview}
                        sx={{ width: 80, height: 80, objectFit: "cover", borderRadius: "12px",
                              border: "1px solid #e5e7eb", opacity: img.uploading ? 0.5 : 1 }}
                      />
                      {img.uploading && (
                        <CircularProgress size={20} sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }} />
                      )}
                      {!img.uploading && (
                        <IconButton size="small"
                          onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                          sx={{ position: "absolute", top: -6, right: -6, bgcolor: "#fff",
                                border: "1px solid #e5e7eb", p: 0.25, "&:hover": { bgcolor: "#fee2e2" } }}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 14, color: "#ef4444" }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {images.length < 3 && (
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        width: 80, height: 80, borderRadius: "12px", border: "2px dashed #d1d5db",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", gap: 0.5,
                        "&:hover": { borderColor: RED, bgcolor: "#fef2f2" },
                      }}
                    >
                      <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 24, color: "#9ca3af" }} />
                      <Typography fontSize="0.65rem" color="text.secondary">Add photo</Typography>
                    </Box>
                  )}
                </Stack>
                <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImagePick} />
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button variant="outlined" onClick={() => setStep(0)}
                  sx={{ flex: 1, borderRadius: "12px", textTransform: "none", py: 1.1, fontWeight: 600 }}>
                  Back
                </Button>
                <Button variant="contained"
                  onClick={() => setStep(2)}
                  disabled={images.some(i => i.uploading)}
                  sx={{ flex: 2, borderRadius: "12px", fontWeight: 700, textTransform: "none",
                        bgcolor: RED, py: 1.1, "&:hover": { bgcolor: "#b30000" } }}>
                  {images.some(i => i.uploading) ? "Uploading…" : "Continue →"}
                </Button>
              </Stack>
            </Paper>
          )}

          {/* ─── STEP 2: Review & Submit ─────────────────────────────────── */}
          {step === 2 && selectedIssue && (
            <Stack spacing={2}>
              <Paper sx={{
                p: { xs: 2.5, md: 3 }, borderRadius: "20px",
                boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
                border: "1px solid rgba(15,23,42,0.05)",
              }}>
                <Typography fontWeight={700} fontSize="0.74rem" color="text.secondary" mb={1.5} letterSpacing={1.2}>
                  YOUR REPORT
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.25} mb={1.5}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: "12px", bgcolor: "#f1f5f9",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem",
                  }}>{selectedIssue.icon}</Box>
                  <Typography fontWeight={700} fontSize="0.95rem">{selectedIssue.label}</Typography>
                </Stack>
                {description && (
                  <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", mb: 1.5 }}>
                    <Typography fontSize="0.85rem" color="#334155">{description}</Typography>
                  </Box>
                )}
                {images.filter(i => i.url).length > 0 && (
                  <Box>
                    <Typography fontSize="0.8rem" fontWeight={600} mb={0.75}>Photos attached</Typography>
                    <Stack direction="row" spacing={1}>
                      {images.filter(i => i.url).map((img, i) => (
                        <Box key={i} component="img" src={img.preview}
                          sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: "10px", border: "1px solid #e5e7eb" }} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Paper>

              <Paper sx={{
                p: { xs: 2, md: 2.5 }, borderRadius: "20px",
                bgcolor: "#eff6ff",
                border: "1px solid #bfdbfe",
              }}>
                <Typography fontWeight={700} fontSize="0.86rem" color="#1d4ed8" mb={1}>What happens next</Typography>
                <Stack spacing={0.8}>
                  <Typography fontSize="0.85rem" color="#1e3a8a">
                    1. Rush My Food support reviews your report (up to 24h)
                  </Typography>
                  <Typography fontSize="0.85rem" color="#1e3a8a">
                    2. If approved, you choose: back to your original payment, or RMF Wallet credit
                  </Typography>
                  <Typography fontSize="0.85rem" color="#1e3a8a">
                    3. Card refunds take 3–8 business days — credit is ready right away
                  </Typography>
                </Stack>
              </Paper>

              <Stack direction="row" spacing={1.5}>
                <Button variant="outlined" onClick={() => setStep(1)}
                  sx={{ flex: 1, borderRadius: "12px", textTransform: "none", py: 1.1, fontWeight: 600 }}>
                  Back
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}
                  sx={{ flex: 2, borderRadius: "12px", fontWeight: 700, textTransform: "none",
                        bgcolor: "#0f172a", py: 1.1, "&:hover": { bgcolor: "#1e293b" } }}>
                  {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : isFollowUp ? "Add follow-up" : "Submit report"}
                </Button>
              </Stack>
            </Stack>
          )}

          {/* ─── STEP 3: Success ─────────────────────────────────────────── */}
          {step === 3 && createdTicket && (
            <Paper sx={{
              p: { xs: 3.5, md: 5 }, borderRadius: "22px", textAlign: "center",
              boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              border: "1px solid rgba(15,23,42,0.05)",
            }}>
              <Box sx={{
                width: 84, height: 84, borderRadius: "50%",
                bgcolor: "#dcfce7", color: "#16a34a",
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 2,
              }}>
                <CheckCircleIcon sx={{ fontSize: 48 }} />
              </Box>
              <Typography variant="h6" fontWeight={800} mb={0.5}>Report submitted</Typography>
              <Chip label={`Ticket ${createdTicket.ticketNumber}`} size="small"
                sx={{ fontWeight: 700, bgcolor: "#f1f5f9", color: "#334155", mb: 2 }} />
              <Typography fontSize="0.88rem" color="text.secondary" mb={3}>
                We've sent your report for review. You'll be notified when it's resolved.
              </Typography>
              <Stack spacing={1.25} sx={{ maxWidth: 320, mx: "auto" }}>
                <Button variant="contained" onClick={() => navigate(`/ticket-detail/${createdTicket.id}`)}
                  sx={{ borderRadius: "12px", fontWeight: 700, textTransform: "none",
                        bgcolor: RED, py: 1.1, "&:hover": { bgcolor: "#b30000" } }}>
                  View ticket status
                </Button>
                <Button variant="outlined" onClick={() => navigate("/orders")}
                  sx={{ borderRadius: "12px", textTransform: "none", py: 1.1, fontWeight: 600 }}>
                  Back to my orders
                </Button>
              </Stack>
            </Paper>
          )}
        </Container>
      </Box>
    </>
  );
}
